import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, AlertTriangle, Info } from 'lucide-react';

const TermeniConditii = () => {
  // Scroll to top când pagina se încarcă
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="flex items-center mb-8">
            <FileText className="w-12 h-12 text-emerald-600 mr-4" />
            <h1 className="text-4xl font-bold text-gray-900">Termeni și Condiții</h1>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Data ultimei actualizări:</strong> 21 octombrie 2026
            </p>

            <p className="text-gray-700 mb-6">
              Prin prezenta, BITCRAFTERS SRL vă informăm cu privire la termenii și condițiile de utilizare a Platformei.
            </p>

            <p className="text-gray-700 mb-6">
              Prezentele condiții pentru utilizarea site-ului <strong>cf-extras.ro</strong>, împreună cu subdomeniile acestuia și
              pentru aplicația online, sunt încheiate între{' '}
              <strong>BITCRAFTERS SRL</strong>{' '}
              (CUI: RO49900684, Nr. Reg. Com.: J2024007383407) și
              orice persoană care utilizează și/sau vizualizează conținutul site-ului și aplicațiilor sale.
            </p>

            <p className="text-gray-700 mb-8">
              Persoanele care utilizează și/sau vizualizează site-ul <strong>cf-extras.ro</strong> sunt considerate persoane care
              acceptă și respectă prezentele Termeni și Condiții.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-8">
              <p className="text-sm text-yellow-800">
                <strong><AlertTriangle className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Important:</strong> Vă rugăm să citiți acești Termeni și Condiții cu atenție înainte de a utiliza platforma.
                Prin utilizarea serviciilor noastre, confirmați că ați citit, înțeles și acceptat integral prevederile prezentului acord.
              </p>
            </div>

            {/* 1. DEFINIȚII */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Definiții</h2>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>
                <strong>"Platforma"</strong> - site-ul web cf-extras.ro, împreună cu subdomeniile sale și aplicația online,
                aflate în proprietatea BITCRAFTERS SRL
              </li>
              <li>
                <strong>"Operator"</strong> - BITCRAFTERS SRL,
                CUI RO49900684, cu sediul social în Str. Fratii Golescu 4, București, Sector 1
              </li>
              <li>
                <strong>"Utilizator"</strong> - orice persoană fizică sau juridică care accesează și utilizează Platforma pentru
                obținerea de extrase carte funciară
              </li>
              <li>
                <strong>"Vizitator"</strong> - orice persoană fizică sau juridică care vizionează una sau mai multe pagini din site
              </li>
              <li>
                <strong>"Extras Carte Funciară"</strong> - document oficial eliberat prin intermediul Platformei,
                care atestă situația juridică a unui imobil (proprietar, sarcini, ipoteci, servituți)
              </li>
              <li>
                <strong>"Comandă"</strong> - solicitarea de obținere a unui extras de carte funciară sau plan cadastral pe ortofotoplan, plasată prin completarea formularului
                online și efectuarea plății
              </li>
              <li>
                <strong>"Contract"</strong> - reprezintă acordul de utilizare încheiat la distanță (electronic) între Operator și
                Utilizator/Vizitator, considerat încheiat în momentul utilizării/vizionării conținutului site-ului
              </li>
              <li>
                <strong>"Comunicări comerciale"</strong> - mesaje periodice trimise (e-mail/SMS) conținând informații despre oferte,
                servicii și produse similare sau complementare (doar cu consimțământul explicit al utilizatorului)
              </li>
              <li>
                <strong>"Taxa ANCPI"</strong> - taxa oficială percepută pentru emiterea extrasului de carte funciară sau a planului cadastral, inclusă în
                prețul afișat pe Platformă
              </li>
            </ul>

            <p className="text-gray-700 mb-4">
              Termenii privind datele cu caracter personal (DPO, operator, prelucrare, restricționare, profilare etc.) au înțelesul prevăzut
              de legislația în vigoare și <strong>Regulamentul (UE) 2016/679 (GDPR)</strong>.
            </p>

            {/* 2. ACCEPTAREA TERMENILOR */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Acceptarea Termenilor și Condițiilor</h2>
            <p className="text-gray-700 mb-4">
              Prin utilizarea și înregistrarea pe acest site, acceptați termenii și condițiile de utilizare menționate mai jos.
              Vă recomandăm să le citiți cu atenție.
            </p>
            <p className="text-gray-700 mb-4">
              Prin inițierea unei tranzacții, confirmați implicit că îndepliniți concomitent următoarele condiții:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 mb-4 space-y-2">
              <li>Acceptați prețul prezentat pe site, care include taxa ANCPI și TVA</li>
              <li>Veți primi pe email extrasul de carte funciară sau planul cadastral, însoțit de facturile aferente (factură fiscală + chitanță ANCPI),
                  în termenul estimat afișat pe platformă (5 minute - 24 ore). Acest termen poate fi prelungit în funcție de
                  disponibilitatea Portalului ANCPI, fără ca Operatorul să fie răspunzător de eventualele întârzieri</li>
              <li>Împuterniciți Operatorul să obțină în numele dumneavoastră extrasul de carte funciară sau planul cadastral online prin intermediul
                  portalului ANCPI</li>
              <li>Declarați pe propria răspundere că v-ați informat cu privire la tipul de extras de care aveți nevoie,
                  iar solicitarea făcută este conformă cu nevoile dumneavoastră. Operatorul nu poate fi făcut răspunzător în cazul
                  în care autoritățile la care prezentați extrasul nu îl acceptă</li>
              <li>Confirmați că datele furnizate (număr cadastral, județ, UAT, date facturare etc.) sunt corecte și vă aparțin sau sunteți autorizat
                  să le utilizați pentru obținerea extrasului</li>
            </ol>

            {/* 3. SERVICII OFERITE */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Servicii Oferite</h2>
            <p className="text-gray-700 mb-4">
              BITCRAFTERS SRL, prin platforma <strong>cf-extras.ro</strong>,
              oferă posibilitatea utilizatorilor de a obține online extrase de carte funciară și planuri cadastrale pe ortofotoplan,
              inclusiv:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>
                <strong>Extras Carte Funciară</strong> - document oficial care atestă situația juridică actuală a unui imobil
                (proprietar, sarcini, ipoteci, servituți, suprafață, destinație)
              </li>
              <li>
                <strong>Extras pe Ortofotoplan</strong> - plan cadastral oficial cu reprezentare grafică pe ortofotoplan,
                care indică amplasamentul și limitele imobilului
              </li>
            </ul>

            <p className="text-gray-700 mb-4">
              <strong>Taxa ANCPI este inclusă în prețul afișat</strong> pe platformă. Extrasele sunt livrate în format electronic (PDF),
              cu semnătură electronică extinsă, având aceeași valoare juridică ca și extrasele obținute direct de la ghișee.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong><Info className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Notă importantă:</strong> BITCRAFTERS SRL acționează exclusiv ca platformă
                de intermediere tehnică. Extrasele de carte funciară și planurile cadastrale sunt emise
                <strong> exclusiv de ANCPI</strong>, autoritate publică română.
                Platforma noastră facilitează procesul de solicitare online, validare date, plată
                și livrare electronică a documentelor oficiale.
              </p>
            </div>

            {/* 4. PROCESUL DE COMANDĂ */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Procesul de Comandă</h2>
            <p className="text-gray-700 mb-4">
              Pentru a plasa o comandă, utilizatorul trebuie să parcurgă următorii pași:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 mb-4 space-y-2">
              <li>
                <strong>Selectare tip extras</strong> - Alegeți între Extras Carte Funciară sau Extras pe Ortofotoplan
              </li>
              <li>
                <strong>Introducere date imobil</strong> - Județ, UAT (Unitate Administrativ-Teritorială) și număr cadastral
                (cu verificare automată a existenței și statusului)
              </li>
              <li>
                <strong>Introducere date facturare</strong> - Facturare pe firmă (CUI) sau pe persoană fizică (nume, adresă)
              </li>
              <li>
                <strong>Introducere date contact</strong> - Email (pentru livrare extras + facturi) și telefon (pentru notificări SMS)
              </li>
              <li>
                <strong>Verificare informații</strong> - Revizuirea tuturor datelor introduse înainte de plată
              </li>
              <li>
                <strong>Acceptare termeni și condiții</strong> - Confirmare că ați citit și acceptat prezentele Termeni și Condiții,
                Politica de Confidențialitate și că înțelegeți că livrarea va începe imediat, pierzând dreptul de retragere conform
                OUG 34/2014
              </li>
              <li>
                <strong>Plată securizată</strong> - Efectuarea plății online prin card bancar, procesată securizat prin Stripe
                (SSL encryption 256-bit)
              </li>
              <li>
                <strong>Confirmare comandă</strong> - Primire email automat de confirmare + SMS cu detalii comandă și status procesare
              </li>
            </ol>

            {/* 5. PREȚURI ȘI PLATĂ */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Prețuri și Plată</h2>
            <p className="text-gray-700 mb-4">
              Prețurile afișate pe Platformă sunt exprimate în <strong>LEI (RON)</strong> și includ:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Taxa ANCPI</strong> - taxa oficială pentru emiterea extrasului de carte funciară sau a planului cadastral</li>
              <li><strong>Taxa de intermediere</strong> - pentru serviciile de procesare automată, validare date și livrare electronică</li>
              <li><strong>TVA</strong> - conform legislației în vigoare (afișat distinct pe factură)</li>
            </ul>

            <p className="text-gray-700 mb-4">
              <strong>Plata se efectuează online</strong> prin card bancar (Visa, Mastercard, American Express), procesată securizat prin
              <strong> Stripe</strong>, unul dintre cei mai siguri procesatori de plăți la nivel global. Datele cardului dumneavoastră
              sunt criptate SSL 256-bit și <strong>NU sunt stocate pe serverele noastre</strong>.
            </p>

            <p className="text-gray-700 mb-4">
              După confirmarea plății, veți primi:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Email confirmare plată (Stripe)</li>
              <li>SMS confirmare comandă cu status procesare</li>
              <li>Email cu extrasul de carte funciară / planul cadastral + factură fiscală + chitanță ANCPI (la finalizare)</li>
            </ul>

            {/* 6. POLITICA DE LIVRARE */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Politica de Livrare</h2>
            <p className="text-gray-700 mb-4">
              Comenzile sunt livrate <strong>în format electronic (PDF)</strong>, pe adresa de email indicată de utilizator, după
              aproximativ <strong>5 minute</strong> de la confirmarea plății.
            </p>

            <p className="text-gray-700 mb-4">
              <strong>Timpul de livrare depinde de:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Disponibilitatea Portalului ANCPI</li>
              <li>Volumul de comenzi (în perioadele de vârf, timpul poate fi prelungit)</li>
            </ul>

            <p className="text-gray-700 mb-4">
              <strong>Ce primiți pe email:</strong>
            </p>
            <ol className="list-decimal pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Extras Carte Funciară / Plan Cadastral</strong> - document oficial PDF cu semnătură electronică extinsă</li>
              <li><strong>Chitanță ANCPI</strong> - chitanță oficială pentru taxa de emitere extras</li>
              <li><strong>Factură fiscală</strong> - factură emisă de BITCRAFTERS SRL pentru
                  serviciile de intermediere</li>
            </ol>

            <p className="text-gray-700 mb-4">
              <strong>Notificări SMS:</strong> Veți primi SMS automat la numărul de telefon indicat când extrasul este emis și gata pentru descărcare.
            </p>

            <p className="text-gray-700 mb-4">
              În cazuri excepționale, în care Portalul ANCPI nu este funcțional (mentenanță, probleme tehnice),
              timpul de livrare se poate extinde până la <strong>48 ore</strong>. Veți fi notificat prin email în astfel de situații.
            </p>

            {/* 7. DREPT DE RETRAGERE */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Drept de Retragere</h2>
            <p className="text-gray-700 mb-4">
              Conform <strong>OUG 34/2014</strong> (transpunerea Directivei UE 2011/83), Art. 16 lit. m), utilizatorul
              <strong> NU beneficiază de dreptul de retragere</strong> în cazul:
            </p>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <p className="text-sm text-red-800">
                <strong>"furnizarea de conținut digital care nu este livrat pe un suport material, în cazul în care prestarea a început
                cu acordul prealabil expres al consumatorului și după ce acesta a confirmat că a luat cunoștință de faptul că
                își va pierde dreptul de retragere."</strong>
              </p>
            </div>

            <p className="text-gray-700 mb-4">
              Prin bifarea checkbox-ului <strong>"Confirm că livrarea va începe imediat și înțeleg că pierd dreptul de retragere conform
              OUG 34/2014"</strong> în formularul de comandă (pasul 4 - Confirmare), declarați explicit că:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Solicitați livrarea imediată a extrasului (fără perioada de așteptare de 14 zile)</li>
              <li>Înțelegeți și acceptați că <strong>pierdeți dreptul de retragere</strong> pentru această comandă</li>
              <li>Extrasul va fi procesat automat imediat după confirmarea plății</li>
            </ul>

            <p className="text-gray-700 mb-4">
              <strong>Excepție - Rambursare în caz de imposibilitate tehnică:</strong>
            </p>
            <p className="text-gray-700 mb-4">
              În cazul în care, din motive imputabile Operatorului sau din cauza unor probleme tehnice majore ale Platformei,
              extrasul NU poate fi livrat în termen de <strong>48 ore</strong> de la confirmarea plății, utilizatorul poate solicita
              <strong> rambursarea integrală</strong> a sumei plătite.
            </p>

            <p className="text-gray-700 mb-4">
              Cererile de rambursare se trimit la adresa de email: <strong>contact@cf-extras.ro</strong>,
              specificând numărul comenzii și motivul solicitării. Rambursarea se va efectua în termen de maxim <strong>14 zile lucrătoare</strong>,
              prin returnarea sumei pe cardul utilizat pentru plată.
            </p>

            {/* 8. PROTECȚIA DATELOR CU CARACTER PERSONAL */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Protecția Datelor cu Caracter Personal</h2>
            <p className="text-gray-700 mb-4">
              Datele personale ale utilizatorilor sunt prelucrate conform <strong>Politicii de Confidențialitate</strong> și
              <strong> Regulamentului (UE) 2016/679 (GDPR)</strong>.
            </p>

            <p className="text-gray-700 mb-4">
              BITCRAFTERS SRL are calitatea de <strong>operator de date</strong> și
              prelucră datele dumneavoastră cu caracter personal în următoarele scopuri:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Procesarea comenzilor și emiterea extraselor de carte funciară (obligație contractuală)</li>
              <li>Emiterea facturilor fiscale (obligație legală)</li>
              <li>Livrarea extraselor prin email și SMS (obligație contractuală)</li>
              <li>Procesarea plăților prin Stripe (obligație contractuală)</li>
              <li>Comunicări de marketing (doar cu consimțământul dumneavoastră explicit)</li>
            </ul>

            <p className="text-gray-700 mb-4">
              <strong>Procesatori autorizați (parteneri care prelucrează datele în numele nostru):</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Stripe Inc.</strong> (SUA) - procesare plăți card, transfer legal via Standard Contractual Clauses (SCCs)</li>
              <li><strong>NewsMAN - Dazoot Software SRL</strong> (România) - livrare email tranzacțional cu atașamente PDF</li>
              <li><strong>Twilio Inc.</strong> (SUA) - livrare SMS notificări, transfer legal via SCCs</li>
              <li><strong>sendSMS Solutions SRL</strong> (România) - livrare SMS notificări (furnizor de rezervă)</li>
              <li><strong>Oblio</strong> (România) - emitere facturi fiscale automate</li>
              <li><strong>ANCPI</strong> (România) - autoritate publică, validare număr cadastral și emitere extrase</li>
            </ul>

            <p className="text-gray-700 mb-4">
              Pentru detalii complete despre prelucrarea datelor, drepturile dumneavoastră (acces, rectificare, ștergere, opoziție,
              portabilitate), perioada de stocare și modalitatea de exercitare a drepturilor, vă rugăm să consultați
              <strong> Politica de Confidențialitate</strong>.
            </p>

            <p className="text-gray-700 mb-4">
              Contact pentru exercitarea drepturilor GDPR: <strong>dpo@cf-extras.ro</strong>
            </p>

            {/* 9. RĂSPUNDERE ȘI LIMITĂRI */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Răspundere și Limitări</h2>
            <p className="text-gray-700 mb-4">
              BITCRAFTERS SRL <strong>nu își asumă răspunderea</strong> pentru:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>
                <strong>Informații eronate furnizate de utilizator</strong> - număr cadastral incorect, județ/UAT greșit, email invalid etc.
                Utilizatorul este responsabil pentru corectitudinea datelor introduse
              </li>
              <li>
                <strong>Întârzieri cauzate de Portalul ANCPI</strong> - mentenanță programată, probleme tehnice ale ANCPI,
                indisponibilitate temporară
              </li>
              <li>
                <strong>Probleme tehnice independente de controlul nostru</strong> - căderi servere Stripe, NewsMAN, Twilio, SendSMS,
                probleme de conectivitate internet
              </li>
              <li>
                <strong>Refuzul acceptării extrasului de către autorități</strong> - în cazul în care autoritatea solicitantă
                nu acceptă formatul electronic sau are cerințe specifice nespecificate la momentul comenzii
              </li>
              <li>
                <strong>Pierderi de profit, de afaceri sau oportunități</strong> - datorate întârzierilor de livrare sau imposibilității
                temporare de procesare
              </li>
            </ul>

            <p className="text-gray-700 mb-4">
              <strong>Limitarea răspunderii:</strong> În măsura permisă de lege, răspunderea noastră este limitată la valoarea
              comenzii plasate. Nu suntem răspunzători pentru prejudicii indirecte, pierderi consecutive sau daune morale.
            </p>

            <p className="text-gray-700 mb-4">
              Niciunul dintre elementele menționate mai sus nu exclude sau limitează răspunderea pentru fraudă, deces, vătămare fizică
              cauzată de neglijență sau orice altă răspundere care nu poate fi limitată sau exclusă de lege.
            </p>

            {/* 10. PROPRIETATE INTELECTUALĂ */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Proprietate Intelectuală</h2>
            <p className="text-gray-700 mb-4">
              Mărcile comerciale, logo-urile, design-ul site-ului, aplicația software, textele, imaginile, graficele și alte materiale
              utilizate pe acest site aparțin exclusiv BITCRAFTERS SRL sau sunt utilizate
              cu permisiunea deținătorilor de drepturi.
            </p>

            <p className="text-gray-700 mb-4">
              <strong>Este strict interzisă:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Copierea, reproducerea, modificarea sau republicarea conținutului site-ului fără acordul scris al Operatorului</li>
              <li>Utilizarea roboților, spider-elor sau altor metode automate pentru accesarea platformei</li>
              <li>Efectuarea de reverse engineering, decompilare sau dezasamblare a aplicației software</li>
              <li>Revânzarea extraselor obținute prin platformă fără acordul Operatorului</li>
            </ul>

            {/* 11. SOLUȚIONAREA LITIGIILOR */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Soluționarea Litigiilor</h2>
            <p className="text-gray-700 mb-4">
              Eventualele litigii decurgând din sau în legătură cu prezentele Termeni și Condiții vor fi soluționate pe cale amiabilă
              între părți, prin negocieri directe.
            </p>

            <p className="text-gray-700 mb-4">
              În cazul în care nu se ajunge la o înțelegere amiabilă în termen de <strong>30 zile</strong> de la notificarea litigiului,
              acesta va fi soluționat de instanțele judecătorești competente din <strong>România</strong>, conform legislației române.
            </p>

            <p className="text-gray-700 mb-4">
              <strong>Platformă Europeană de Soluționare Online a Litigiilor (SOL):</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Consumatorii pot apela la platforma europeană de soluționare online a litigiilor, disponibilă la adresa:{' '}
              <a
                href="https://ec.europa.eu/consumers/odr"
                className="text-emerald-600 hover:underline font-semibold"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://ec.europa.eu/consumers/odr
              </a>
            </p>

            <p className="text-gray-700 mb-4">
              <strong>Autoritatea Națională pentru Protecția Consumatorilor (ANPC):</strong> Pentru reclamații privind serviciile de
              comerț electronic, consumatorii pot sesiza ANPC la adresa:{' '}
              <a
                href="https://anpc.ro"
                className="text-emerald-600 hover:underline font-semibold"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://anpc.ro
              </a>
            </p>

            {/* 12. MODIFICĂRI ALE TERMENILOR */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Modificări ale Termenilor și Condițiilor</h2>
            <p className="text-gray-700 mb-4">
              Ne rezervăm dreptul de a modifica acești Termeni și Condiții în orice moment, pentru a reflecta schimbări în legislație,
              servicii oferite sau practici de afaceri.
            </p>

            <p className="text-gray-700 mb-4">
              <strong>Modificările vor fi comunicate</strong> prin:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Publicarea versiunii actualizate pe această pagină (cu data ultimei actualizări afișată sus)</li>
              <li>Email către utilizatorii înregistrați (pentru modificări majore)</li>
              <li>Banner pe pagina principală a platformei (pentru modificări esențiale)</li>
            </ul>

            <p className="text-gray-700 mb-4">
              Modificările vor intra în vigoare <strong>imediat după publicare</strong> pe site. Utilizarea continuă a platformei
              după publicarea modificărilor constituie acceptarea noilor termeni.
            </p>

            <p className="text-gray-700 mb-4">
              Dacă nu sunteți de acord cu modificările aduse, vă rugăm să încetați utilizarea platformei și să ne contactați
              pentru clarificări.
            </p>

            {/* 13. CONTACT */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Contact</h2>
            <p className="text-gray-700 mb-4">
              Pentru orice nelămuriri, întrebări sau solicitări cu privire la prezentele Termeni și Condiții, ne puteți contacta la:
            </p>

            <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
              <p className="text-gray-700 mb-2">
                <strong>BITCRAFTERS SRL</strong>
              </p>
              <p className="text-gray-700">
                <strong>CUI:</strong> RO49900684
              </p>
              <p className="text-gray-700">
                <strong>Nr. Reg. Com.:</strong> J2024007383407
              </p>
              <p className="text-gray-700">
                <strong>Adresă:</strong> Str. Fratii Golescu 4, București, Sector 1
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong>{' '}
                <a href="mailto:contact@cf-extras.ro" className="text-emerald-600 hover:underline">
                  contact@cf-extras.ro
                </a>
              </p>
              <p className="text-gray-700">
                <strong>GDPR:</strong>{' '}
                <a href="mailto:dpo@cf-extras.ro" className="text-emerald-600 hover:underline">
                  dpo@cf-extras.ro
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Telefon:</strong>{' '}
                <a href="tel:+40756888282" className="text-emerald-600 hover:underline">
                  +40756888282
                </a>
              </p>
            </div>

            <div className="mt-8 p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                Declarați pe propria răspundere că ați citit integral, ați înțeles și sunteți de acord cu prevederile prezentului acord.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermeniConditii;
